/**
 * Diagnosis Compare — two full months side-by-side (metrics + short AI narrative)
 * plus a saved-report comparison (two stored AI reports side-by-side).
 */
import React, { useState, useCallback, useEffect } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Loader2, ArrowLeftRight, Sparkles, TrendingUp, TrendingDown, Minus, FileText } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const METRICS = [
  { key: 'commission_myr', label: 'Commission', unit: 'RM', better: 'up' },
  { key: 'deposit_usd', label: 'Deposit', unit: '$', better: 'up' },
  { key: 'active_client', label: 'Active Client', unit: '', better: 'up' },
  { key: 'traded_lot', label: 'Traded Lot', unit: 'lot', better: 'up' },
  { key: 'leads_sales', label: 'Leads (Sales)', unit: '', better: 'up' },
  { key: 'closed', label: 'Closed', unit: '', better: 'up' },
  { key: 'closing_rate_pct', label: 'Closing Rate', unit: '%', better: 'up' },
  { key: 'subs', label: 'Subscribers', unit: '', better: 'up' },
  { key: 'spend_myr', label: 'Ad Spend', unit: 'RM', better: 'down' },
  { key: 'cps_myr', label: 'CPS (Cost/Sub)', unit: 'RM', better: 'down' },
  { key: 'cpl_myr', label: 'CPL (Cost/Lead)', unit: 'RM', better: 'down' },
  { key: 'com_per_lot_usd', label: 'Com / Lot', unit: '$', better: 'up' },
  { key: 'avg_lot_per_client', label: 'Avg Lot / Client', unit: 'lot', better: 'up' },
];

const fmtVal = (v, unit) => {
  if (v === null || v === undefined) return '—';
  const n = Number(v);
  const s = Math.abs(n) >= 1000 ? n.toLocaleString('en-MY', { maximumFractionDigits: 0 }) : n.toLocaleString('en-MY', { maximumFractionDigits: 2 });
  if (unit === 'RM') return `RM ${s}`;
  if (unit === '$') return `$${s}`;
  if (unit === '%') return `${s}%`;
  return unit ? `${s} ${unit}` : s;
};

function DeltaPill({ pct, better }) {
  if (pct === null || pct === undefined) return <span className="text-[hsl(var(--text-muted))] text-[10px]">—</span>;
  const good = better === 'down' ? pct < 0 : pct > 0;
  const flat = Math.abs(pct) < 1;
  const Icon = flat ? Minus : pct > 0 ? TrendingUp : TrendingDown;
  const color = flat ? '#94a3b8' : good ? '#10b981' : '#ef4444';
  return (
    <span className="inline-flex items-center gap-1 text-[11px] font-bold font-mono" style={{ color }}>
      <Icon size={11} /> {pct > 0 ? '+' : ''}{pct.toFixed(1)}%
    </span>
  );
}

export default function DiagnosisCompareTab({ ibs, monthOptions, fmtPeriod, shiftPeriod, toPeriod }) {
  const [ibId, setIbId] = useState(ibs[0]?.id || '');
  const [periodA, setPeriodA] = useState(() => shiftPeriod(toPeriod(new Date()), -1));
  const [periodB, setPeriodB] = useState(() => shiftPeriod(toPeriod(new Date()), -2));
  const [withAi, setWithAi] = useState(true);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  // Saved-report comparison
  const [history, setHistory] = useState([]);
  const [repAId, setRepAId] = useState('');
  const [repBId, setRepBId] = useState('');
  const [repA, setRepA] = useState(null);
  const [repB, setRepB] = useState(null);
  const [repLoading, setRepLoading] = useState(false);

  useEffect(() => { if (!ibId && ibs[0]?.id) setIbId(ibs[0].id); }, [ibs, ibId]);

  const loadHistory = useCallback(async (id) => {
    if (!id) return;
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/api/diagnosis/history/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      setHistory(res.data.reports || []);
      setRepAId(''); setRepBId(''); setRepA(null); setRepB(null);
    } catch (e) { setHistory([]); }
  }, []);

  useEffect(() => { loadHistory(ibId); }, [ibId, loadHistory]);

  const runCompare = async () => {
    if (!ibId) { toast.error('Pilih channel dahulu'); return; }
    if (periodA === periodB) { toast.error('Pilih dua bulan yang berbeza'); return; }
    setLoading(true);
    setData(null);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(
        `${API}/api/diagnosis/compare/${ibId}?period_a=${periodA}&period_b=${periodB}&ai=${withAi}`,
        { headers: { Authorization: `Bearer ${token}` }, timeout: 90000 },
      );
      setData(res.data);
    } catch (err) {
      toast.error('Compare gagal: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  const loadReport = async (id, which) => {
    if (!id) { which === 'a' ? setRepA(null) : setRepB(null); return; }
    setRepLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/api/diagnosis/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      which === 'a' ? setRepA(res.data) : setRepB(res.data);
    } catch (e) {
      toast.error('Gagal load report');
    } finally {
      setRepLoading(false);
    }
  };

  const selectCls = 'h-8 px-2 text-[11px] bg-[hsl(var(--surface-elevated))] border border-[hsl(var(--border))] rounded-md text-white outline-none cursor-pointer';

  return (
    <div className="space-y-4" data-testid="diagnosis-compare-tab">
      {/* ===== Controls ===== */}
      <div className="bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-lg p-4 flex flex-wrap items-end gap-3">
        <div className="flex flex-col gap-1">
          <label className="text-[9px] uppercase font-bold text-[hsl(var(--text-muted))] tracking-wider">Channel</label>
          <select value={ibId} onChange={(e) => setIbId(e.target.value)} className={selectCls} data-testid="compare-channel-select">
            {ibs.map((ib) => <option key={ib.id} value={ib.id} className="bg-[#0a0a0f]">{ib.name}</option>)}
          </select>
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-[9px] uppercase font-bold text-[hsl(var(--text-muted))] tracking-wider">Bulan A</label>
          <select value={periodA} onChange={(e) => setPeriodA(e.target.value)} className={selectCls} data-testid="compare-period-a">
            {monthOptions.map((p) => <option key={p} value={p} className="bg-[#0a0a0f]">{fmtPeriod(p)}</option>)}
          </select>
        </div>
        <ArrowLeftRight size={14} className="text-[hsl(var(--text-muted))] mb-2" />
        <div className="flex flex-col gap-1">
          <label className="text-[9px] uppercase font-bold text-[hsl(var(--text-muted))] tracking-wider">Bulan B</label>
          <select value={periodB} onChange={(e) => setPeriodB(e.target.value)} className={selectCls} data-testid="compare-period-b">
            {monthOptions.map((p) => <option key={p} value={p} className="bg-[#0a0a0f]">{fmtPeriod(p)}</option>)}
          </select>
        </div>
        <label className="flex items-center gap-1.5 text-[11px] text-[hsl(var(--text-muted))] mb-1.5 cursor-pointer">
          <input type="checkbox" checked={withAi} onChange={(e) => setWithAi(e.target.checked)} data-testid="compare-ai-toggle" />
          Ringkasan AI
        </label>
        <Button
          size="sm"
          className="h-8 text-[11px] bg-gradient-to-r from-[#E63946] to-[#c62828] hover:brightness-110"
          onClick={runCompare}
          disabled={loading}
          data-testid="compare-run-btn"
        >
          {loading ? <Loader2 size={12} className="mr-1 animate-spin" /> : <ArrowLeftRight size={12} className="mr-1" />}
          Compare
        </Button>
      </div>

      {loading && (
        <div className="p-10 text-center" data-testid="compare-loading">
          <Loader2 className="animate-spin text-[#E63946] mx-auto mb-2" size={22} />
          <div className="text-[11px] text-[hsl(var(--text-muted))]">Kira metrik dua bulan{withAi ? ' + ringkasan AI' : ''}...</div>
        </div>
      )}

      {data && (
        <>
          {/* ===== Metric table ===== */}
          <div className="bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-lg overflow-hidden" data-testid="compare-result">
            <div className="px-4 py-3 border-b border-[hsl(var(--border))] flex items-center justify-between flex-wrap gap-2">
              <div className="text-[11px] font-bold text-white">
                {data.ib_name} — {fmtPeriod(data.period_a.period)} <span className="text-[hsl(var(--text-muted))]">vs</span> {fmtPeriod(data.period_b.period)}
              </div>
              <div className="text-[9px] text-[hsl(var(--text-muted))] font-mono">
                {data.period_a.from} → {data.period_a.to} · {data.period_b.from} → {data.period_b.to}
              </div>
            </div>
            <table className="w-full text-[11px]" data-testid="compare-table">
              <thead>
                <tr className="bg-[hsl(var(--surface-elevated))] text-[9px] uppercase text-[hsl(var(--text-muted))]">
                  <th className="py-2 px-3 text-left">Metrik</th>
                  <th className="py-2 px-3 text-right">{fmtPeriod(data.period_a.period)}</th>
                  <th className="py-2 px-3 text-right">{fmtPeriod(data.period_b.period)}</th>
                  <th className="py-2 px-3 text-right w-28">Beza</th>
                </tr>
              </thead>
              <tbody>
                {METRICS.map((m) => {
                  const a = data.period_a.metrics?.[m.key];
                  const b = data.period_b.metrics?.[m.key];
                  const pct = data.comparison?.[m.key]?.delta_pct;
                  return (
                    <tr key={m.key} className="border-t border-[hsl(var(--border))]/40 hover:bg-[hsl(var(--surface-elevated))]" data-testid={`compare-row-${m.key}`}>
                      <td className="py-2 px-3 text-[hsl(var(--text-muted))]">{m.label}</td>
                      <td className="py-2 px-3 text-right font-mono font-bold text-white">{fmtVal(a, m.unit)}</td>
                      <td className="py-2 px-3 text-right font-mono text-[hsl(var(--text-muted))]">{fmtVal(b, m.unit)}</td>
                      <td className="py-2 px-3 text-right"><DeltaPill pct={pct} better={m.better} /></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* ===== AI narrative ===== */}
          {data.ai_summary && (
            <div className="bg-gradient-to-br from-[#7c3aed]/10 via-[#E63946]/6 to-transparent border border-[#7c3aed]/30 rounded-lg p-4 space-y-3" data-testid="compare-ai-summary">
              <div className="flex items-center gap-2">
                <Sparkles size={13} className="text-[#a78bfa]" />
                <div className="text-[10px] uppercase font-bold text-[#a78bfa] tracking-wider">Apa Berubah & Kenapa</div>
              </div>
              {data.ai_summary.kesimpulan && (
                <p className="text-[12px] text-white leading-relaxed">{data.ai_summary.kesimpulan}</p>
              )}
              {(data.ai_summary.ringkasan || []).length > 0 && (
                <ul className="space-y-1.5">
                  {data.ai_summary.ringkasan.map((s, i) => (
                    <li key={i} className="text-[11px] text-[hsl(var(--text-muted))] flex gap-2">
                      <span className="text-[#E63946] font-bold">•</span><span>{s}</span>
                    </li>
                  ))}
                </ul>
              )}
              {(data.ai_summary.punca_utama || []).length > 0 && (
                <div className="pt-2 border-t border-[hsl(var(--border))]/60">
                  <div className="text-[9px] uppercase font-bold text-[hsl(var(--text-muted))] tracking-wider mb-1.5">Punca Utama</div>
                  <ul className="space-y-1">
                    {data.ai_summary.punca_utama.map((s, i) => (
                      <li key={i} className="text-[11px] text-[#fca5a5] flex gap-2"><span>→</span><span>{s}</span></li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* ===== Saved report comparison ===== */}
      <div className="bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-lg p-4 space-y-3" data-testid="compare-saved-reports">
        <div className="flex items-center gap-2">
          <FileText size={13} className="text-[#E63946]" />
          <div className="text-[10px] uppercase font-bold text-[hsl(var(--text-muted))] tracking-wider">Banding Report AI Tersimpan</div>
          <span className="text-[9px] text-[hsl(var(--text-muted))] ml-auto">{history.length} report</span>
        </div>
        {history.length === 0 ? (
          <div className="text-[11px] text-[hsl(var(--text-muted))] italic py-3">Belum ada report tersimpan untuk channel ini.</div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {[['a', repAId, setRepAId], ['b', repBId, setRepBId]].map(([which, val, setVal]) => (
                <select
                  key={which}
                  value={val}
                  onChange={(e) => { setVal(e.target.value); loadReport(e.target.value, which); }}
                  className={selectCls + ' w-full'}
                  data-testid={`compare-report-${which}`}
                >
                  <option value="" className="bg-[#0a0a0f]">— Pilih report {which.toUpperCase()} —</option>
                  {history.map((r) => (
                    <option key={r.id} value={r.id} className="bg-[#0a0a0f]">
                      {new Date(r.generated_at).toLocaleString('en-MY', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })} · {r.period_current}
                    </option>
                  ))}
                </select>
              ))}
            </div>
            {repLoading && <div className="py-4 text-center"><Loader2 size={16} className="animate-spin text-[#E63946] mx-auto" /></div>}
            {(repA || repB) && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {[repA, repB].map((rep, idx) => (
                  <div key={idx} className="border border-[hsl(var(--border))] rounded-lg p-3 space-y-2 bg-[hsl(var(--surface-elevated))]/50" data-testid={`compare-report-panel-${idx === 0 ? 'a' : 'b'}`}>
                    {!rep ? (
                      <div className="text-[11px] text-[hsl(var(--text-muted))] italic">Belum pilih report.</div>
                    ) : (
                      <>
                        <div className="text-[10px] font-bold text-white">{rep.period_label || rep.period_current}</div>
                        <div className="text-[9px] text-[hsl(var(--text-muted))]">
                          {new Date(rep.generated_at).toLocaleString('en-MY', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                        </div>
                        <p className="text-[11px] text-white leading-relaxed">{rep.ai_report?.kesimpulan || '—'}</p>
                        {(rep.ai_report?.observations || []).length > 0 && (
                          <ul className="space-y-1">
                            {rep.ai_report.observations.slice(0, 4).map((o, i) => (
                              <li key={i} className="text-[10px] text-[hsl(var(--text-muted))] flex gap-1.5"><span className="text-[#E63946]">•</span><span>{o}</span></li>
                            ))}
                          </ul>
                        )}
                        {(rep.ai_report?.action_plan?.next_7_days || []).length > 0 && (
                          <div className="pt-2 border-t border-[hsl(var(--border))]/60">
                            <div className="text-[9px] uppercase font-bold text-[hsl(var(--text-muted))] tracking-wider mb-1">Action Plan</div>
                            <ul className="space-y-1">
                              {rep.ai_report.action_plan.next_7_days.slice(0, 4).map((it, i) => (
                                <li key={i} className="text-[10px] text-[hsl(var(--text-muted))]">
                                  → {typeof it === 'string' ? it : it.action}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
